import React, { useEffect } from "react";
import {
  Button,
  Box,
  Typography,
  FormControl,
  FormControlLabel,
  Checkbox,
  Paper,
  FormGroup,
  FormHelperText,
  Tooltip,
} from "@mui/material";
import ComponentLoading from "../../App/ComponentLoading";
import { styled } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BaseDialog from "./BaseDialog";

const ActionButton = styled(Button)(() => ({
  borderRadius: 8,
  padding: "8px 16px",
  textTransform: "none",
  fontWeight: 500,
  minWidth: 100,
}));

const CAScheduleCreationDialog = ({
  open,
  onClose,
  onSubmit,
  title,
  schedules = [],
  isLoading,
  formData,
  onFormChange,
  caSettings = [],
}) => {
  const fixedScheduleOptions = [
    { value: "FARegister", label: "FA Register Schedule (optional)" },
    { value: "FASummary", label: "FA Summary Schedule" },
    { value: "FAAdditions", label: "FA Addition Schedule" },
    { value: "FADisposals", label: "FA Disposal Schedule" },
    { value: "HirePurchase", label: "Hire Purchase Schedule" },
    { value: "CASummary", label: "CA Summary" },
  ];

  const caScheduleOptions = [
    ...new Set(
      caSettings
        .map((setting) => setting.caType)
        .filter((caType) => /^S\d+[A-Z]*$/.test(caType || ""))
    ),
  ].map((caType) => ({
    value: `${caType}Schedule`,
    label: `${caType} Capital Allowance Schedule`,
  }));

  const scheduleOptions = [
    ...fixedScheduleOptions.slice(0, 4),
    ...caScheduleOptions,
    ...fixedScheduleOptions.slice(4),
  ];

  // Helper function to check if a schedule type already exists
  const isScheduleTypeExists = (scheduleType) => {
    if (!schedules || schedules.length <= 0) return false;
    return schedules.some((schedule) => schedule.scheduleType === scheduleType);
  };

  // Initialize formData.scheduleTypes as an array if it doesn't exist
  useEffect(() => {
    if (!formData.scheduleTypes) {
      onFormChange("scheduleTypes", []);
    }
  }, [formData, onFormChange]);

  const handleScheduleChange = (event) => {
    const { value, checked } = event.target;
    const currentTypes = formData.scheduleTypes || [];

    if (checked) {
      // Add the schedule type to the array
      onFormChange("scheduleTypes", [...currentTypes, value]);
    } else {
      // Remove the schedule type from the array
      onFormChange(
        "scheduleTypes",
        currentTypes.filter((type) => type !== value)
      );
    }
  };

  const handleSelectAll = (event) => {
    const { checked } = event.target;
    if (checked) {
      // Select all non-disabled options
      const availableTypes = scheduleOptions
        .filter((option) => !isScheduleTypeExists(option.value))
        .map((option) => option.value);
      onFormChange("scheduleTypes", availableTypes);
    } else {
      // Deselect all
      onFormChange("scheduleTypes", []);
    }
  };

  // Check if all available options are selected
  const areAllSelected = () => {
    const availableOptions = scheduleOptions.filter(
      (option) => !isScheduleTypeExists(option.value)
    );
    return (
      availableOptions.length > 0 &&
      availableOptions.every((option) =>
        (formData.scheduleTypes || []).includes(option.value)
      )
    );
  };

  // Check if some options are selected
  const areSomeSelected = () => {
    const availableOptions = scheduleOptions.filter(
      (option) => !isScheduleTypeExists(option.value)
    );
    return (
      availableOptions.some((option) =>
        (formData.scheduleTypes || []).includes(option.value)
      ) && !areAllSelected()
    );
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      closeDisabled={isLoading}
      title={title}
      subtitle='Select CA Schedules to create'
      icon={<AddIcon />}
      paperSx={{ maxWidth: 520 }}
      contentSx={{
        p: { xs: 2, sm: 3 },
        "& .MuiFormControl-root": {
          mb: 0,
          width: "100%",
        },
      }}
      actionsSx={{
        gap: 2,
        justifyContent: "flex-end",
        "& > :not(style) ~ :not(style)": { ml: 0 },
      }}
      actions={
        <>
          <ActionButton
            onClick={onClose}
            disabled={isLoading}
            variant='outlined'
            color='primary'
            sx={{
              borderColor: "grey.400",
              color: "grey.700",
              "&:hover": {
                borderColor: "grey.600",
                backgroundColor: "grey.100",
              },
            }}
          >
            Cancel
          </ActionButton>
          <ActionButton
            onClick={onSubmit}
            disabled={isLoading || !formData.scheduleTypes?.length}
            variant='contained'
            color='primary'
            sx={{
              bgcolor: "primary.main",
              "&:hover": {
                bgcolor: "#4F337A",
                boxShadow: "0px 2px 4px rgba(96, 63, 151, 0.2)",
              },
              "&:disabled": {
                bgcolor: "text.disabled",
                color: "background.paper",
              },
            }}
          >
            Create Selected Schedules
          </ActionButton>
        </>
      }
    >
      {isLoading ? (
        <ComponentLoading />
      ) : (
        <Box component='form' noValidate>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                backgroundColor: "background.paper",
              }}
            >
              <FormControl component='fieldset' fullWidth>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={areAllSelected()}
                        indeterminate={areSomeSelected()}
                        onChange={handleSelectAll}
                        sx={{
                          color: "primary.main",
                          "&.Mui-checked": {
                            color: "primary.main",
                          },
                        }}
                      />
                    }
                    label='Select All Available Schedules'
                    sx={{
                      mb: 1,
                      pb: 1,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                    }}
                  />
                  {scheduleOptions.map((option) => {
                    const isDisabled = isScheduleTypeExists(option.value);
                    return (
                      <FormControlLabel
                        key={option.value}
                        control={
                          <Checkbox
                            checked={(formData.scheduleTypes || []).includes(
                              option.value
                            )}
                            onChange={handleScheduleChange}
                            value={option.value}
                            disabled={isDisabled}
                            sx={{
                              color: "primary.main",
                              "&.Mui-checked": {
                                color: "primary.main",
                              },
                              "&.Mui-disabled": {
                                color: "text.disabled",
                              },
                            }}
                          />
                        }
                        label={
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography
                              color={
                                isDisabled ? "text.disabled" : "text.primary"
                              }
                            >
                              {option.label}
                            </Typography>
                            {isDisabled && (
                              <Tooltip
                                title='Already added to schedule'
                                placement='top'
                              >
                                <CheckCircleOutlineIcon
                                  fontSize='small'
                                  sx={{
                                    ml: 1,
                                    color: "text.disabled",
                                    fontSize: 16,
                                  }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        }
                        sx={{
                          py: 1,
                          "&:hover": {
                            backgroundColor: isDisabled
                              ? "transparent"
                              : "action.hover",
                            borderRadius: 1,
                          },
                          "&.Mui-disabled": {
                            cursor: "not-allowed",
                          },
                        }}
                      />
                    );
                  })}
                </FormGroup>
                <FormHelperText sx={{ mt: 1 }}>
                  {formData.scheduleTypes?.length || 0} schedule(s) selected
                </FormHelperText>
              </FormControl>
            </Paper>
        </Box>
      )}
    </BaseDialog>
  );
};

export default CAScheduleCreationDialog;
